/**
 * v5 剧本生成任务创建页
 * 分步创建流程：版本选择 → 配置（5步）→ 启动
 */

import React, { useState, useCallback, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Radio,
  message,
  Steps,
  Alert,
  Slider,
  Row,
  Col,
  Tag,
} from 'antd'
import { ArrowLeftOutlined, RightOutlined, LeftOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import type { RadioChangeEvent } from 'antd'
import {
  initScriptGenerationTask,
  configureV5ScriptGenerationTask,
  configureV4ScriptGenerationTask,
  startScriptGenerationTask,
  getDefaultPersonaTemplates,
  uploadScriptFile,
} from '@/services/scriptGeneration'
import type { UploadProps } from 'antd'
import { Upload, Progress } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { handleApiError } from '@/utils/errorHandler'
import {
  SCRIPT_GENERATION_CONFIG,
} from '@/constants/scriptGeneration'

const { TextArea } = Input
const { Option } = Select
const { Step } = Steps

// 步骤定义
const STEPS = [
  { title: '版本选择', key: 'version' },
  { title: '叙事配置', key: 'narrative' },
  { title: '空间配置', key: 'space' },
  { title: 'NPC配置', key: 'npc' },
  { title: '规模配置', key: 'scale' },
  { title: '角色选择', key: 'persona' },
]

/**
 * v5 剧本生成任务创建页
 */
const CreateTaskV5: React.FC = () => {
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()
  const [form] = Form.useForm()

  // 当前步骤（0 = 版本选择，1-5 = 配置步骤）
  const [currentStep, setCurrentStep] = useState(0)

  // 任务状态
  const [taskId, setTaskId] = useState<string | null>(null)
  const [version, setVersion] = useState<'4.0' | '5.0'>('5.0')
  const [loading, setLoading] = useState(false)

  // v4 相关状态
  const [inputType, setInputType] = useState<'text' | 'file'>('text')
  const [uploadedFileId, setUploadedFileId] = useState<string | null>(null)
  const [uploadedFileName, setUploadedFileName] = useState<string>('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  // v5 相关状态
  const [personaTemplates, setPersonaTemplates] = useState<{
    playerTemplates: Array<{ key: string; name: string; description: string }>
    actorTemplates: Array<{ key: string; name: string; description: string }>
  }>({ playerTemplates: [], actorTemplates: [] })

  // 加载角色模板
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templates = await getDefaultPersonaTemplates()
        setPersonaTemplates(templates)
      } catch (error) {
        console.error('加载角色模板失败:', error)
      }
    }
    loadTemplates()
  }, [])

  /**
   * 返回列表页
   */
  const handleBack = useCallback(() => {
    navigate('/script-generation')
  }, [navigate])

  /**
   * 版本选择
   */
  const handleVersionChange = useCallback((e: RadioChangeEvent) => {
    setVersion(e.target.value)
  }, [])

  /**
   * 初始化任务
   */
  const handleInitTask = useCallback(async () => {
    const values = form.getFieldsValue(['taskName', 'taskDescription'])
    if (!values.taskName) {
      messageApi.error('请输入任务名称')
      return false
    }

    setLoading(true)
    try {
      const response = await initScriptGenerationTask({
        task_name: values.taskName,
        task_description: values.taskDescription,
        version,
      })
      setTaskId(response.taskId)
      messageApi.success('任务已初始化')
      return true
    } catch (error) {
      handleApiError(error, '任务初始化失败', messageApi)
      return false
    } finally {
      setLoading(false)
    }
  }, [form, version, messageApi])

  /**
   * 下一步
   */
  const handleNext = useCallback(async () => {
    // 第一步：初始化任务
    if (currentStep === 0) {
      const success = await handleInitTask()
      if (success) {
        setCurrentStep(1)
      }
      return
    }

    // 最后一步：提交配置并启动
    if (currentStep === STEPS.length - 1) {
      await handleSubmit()
      return
    }

    // 中间步骤：验证表单并下一步
    try {
      const currentStepKey = STEPS[currentStep].key
      await form.validateFields([currentStepKey])
      setCurrentStep(currentStep + 1)
    } catch {
      // 验证失败，不继续
    }
  }, [currentStep, handleInitTask, form])

  /**
   * 上一步
   */
  const handlePrev = useCallback(() => {
    setCurrentStep(currentStep - 1)
  }, [currentStep])

  /**
   * 根据规模生成默认角色
   */
  const generateDefaultPersonas = useCallback((
    scale: { total_player_count: number; total_actor_count: number },
    playerTemplateKey: string,
    actorTemplateKey: string
  ) => {
    const players = Array.from({ length: scale.total_player_count }, (_, i) => ({
      template_key: playerTemplateKey,
      custom_name: `玩家${i + 1}`,
    }))

    const actors = Array.from({ length: scale.total_actor_count }, (_, i) => ({
      template_key: actorTemplateKey,
      custom_name: `演员${i + 1}`,
    }))

    return { players, actors }
  }, [])

  /**
   * 提交配置
   */
  const handleSubmit = useCallback(async () => {
    if (!taskId) return

    setLoading(true)
    try {
      const allValues = form.getFieldsValue()

      if (version === '5.0') {
        // v5 配置
        const { narrative, space, npc, scale } = allValues

        // 如果字段未填写，使用默认值
        const safeNarrative = narrative || {
          target_audience: '18-35岁',
          difficulty_level: 'medium',
          writing_style: 'modern',
          theme_tags: []
        }
        const safeSpace = space || {
          public_space_range: [3, 5],
          private_space_range: [5, 10],
          faction_count_range: [2, 4]
        }
        const safeNpc = npc || {
          mode: 'optional',
          count_range: [2, 3]
        }
        const safeScale = scale || {
          total_player_count: 8,
          total_actor_count: 4,
          group_count: 2
        }

        // 生成默认角色
        const defaultPersonas = generateDefaultPersonas(
          safeScale,
          personaTemplates.playerTemplates[0]?.key || 'romance_player',
          personaTemplates.actorTemplates[0]?.key || 'romance_actor'
        )

        await configureV5ScriptGenerationTask(taskId, {
          narrative: safeNarrative,
          space: safeSpace,
          npc: safeNpc,
          scale: safeScale,
          default_personas: defaultPersonas,
        })

        messageApi.success('v5 配置已保存')
      } else {
        // v4 配置
        await configureV4ScriptGenerationTask(taskId, {
          input_type: inputType,
          input_text: inputType === 'text' ? allValues.inputContent : undefined,
          input_file_url: inputType === 'file' ? uploadedFileId! : undefined,
          input_file_name: inputType === 'file' ? uploadedFileName : undefined,
        })

        messageApi.success('v4 配置已保存')
      }

      // 启动任务
      await startScriptGenerationTask(taskId)
      messageApi.success('任务已启动')

      // 跳转到详情页
      navigate(`/script-generation/${taskId}`)
    } catch (error) {
      handleApiError(error, '配置提交失败', messageApi)
    } finally {
      setLoading(false)
    }
  }, [taskId, version, form, inputType, uploadedFileId, uploadedFileName, navigate, messageApi, generateDefaultPersonas, personaTemplates])

  /**
   * 文件上传
   */
  const uploadProps: UploadProps = {
    name: 'file',
    accept: SCRIPT_GENERATION_CONFIG.ALLOWED_FILE_EXTENSIONS.join(','),
    maxCount: 1,
    beforeUpload: (file) => {
      if (file.size > SCRIPT_GENERATION_CONFIG.MAX_FILE_SIZE) {
        messageApi.error(`文件大小不能超过 ${SCRIPT_GENERATION_CONFIG.MAX_FILE_SIZE / 1024 / 1024}MB`)
        return Upload.LIST_IGNORE
      }

      setUploading(true)
      setUploadProgress(0)

      uploadScriptFile(file, (progress) => {
        setUploadProgress(progress)
      })
        .then((result) => {
          setUploadedFileId(result.file_id)
          setUploadedFileName(result.file_name)
          messageApi.success('文件上传成功')
        })
        .catch((error) => {
          handleApiError(error, '文件上传失败', messageApi)
        })
        .finally(() => {
          setUploading(false)
        })

      return false
    },
  }

  /**
   * 渲染步骤内容
   */
  const renderStepContent = () => {
    // 步骤 0: 版本选择
    if (currentStep === 0) {
      return (
        <>
          <Form.Item
            label="任务名称"
            name="taskName"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="例如：赛博朋克悬疑剧本" />
          </Form.Item>

          <Form.Item label="任务描述" name="taskDescription">
            <TextArea rows={3} placeholder="简要描述剧本需求（可选）" />
          </Form.Item>

          <Form.Item label="选择版本" required>
            <Radio.Group value={version} onChange={handleVersionChange}>
              <Radio.Button value="4.0">
                <Space direction="vertical" size={0} style={{ padding: '8px 0' }}>
                  <strong>v4.0 - 文本驱动</strong>
                  <span style={{ fontSize: 12, color: '#666' }}>通过文本描述生成剧本，适合快速体验</span>
                </Space>
              </Radio.Button>
              <Radio.Button value="5.0">
                <Space direction="vertical" size={0} style={{ padding: '8px 0' }}>
                  <strong>v5.0 - 配置驱动（推荐）</strong>
                  <span style={{ fontSize: 12, color: '#666' }}>通过结构化配置精确控制，支持灵活规模</span>
                </Space>
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </>
      )
    }

    // v4 版本：直接显示文本/文件输入
    if (version === '4.0') {
      return (
        <>
          <Alert
            message="v4.0 配置"
            description="通过文本描述或上传文档生成剧本"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item label="输入类型">
            <Radio.Group value={inputType} onChange={(e) => setInputType(e.target.value)}>
              <Radio.Button value="text">文本输入</Radio.Button>
              <Radio.Button value="file">文档上传</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {inputType === 'text' && (
            <Form.Item
              label="剧本描述"
              name="inputContent"
              rules={[{ required: true, message: '请输入剧本描述' }]}
            >
              <TextArea
                rows={12}
                placeholder="描述你的剧本创意、背景、角色、情节等..."
              />
            </Form.Item>
          )}

          {inputType === 'file' && (
            <Form.Item label="上传文档">
              {uploading ? (
                <Progress percent={uploadProgress} status="active" />
              ) : (
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>选择文件</Button>
                </Upload>
              )}
              {uploadedFileName && (
                <Tag color="success" style={{ marginTop: 8 }}>
                  已上传: {uploadedFileName}
                </Tag>
              )}
            </Form.Item>
          )}
        </>
      )
    }

    // v5 版本：分步配置
    const stepKey = STEPS[currentStep].key

    switch (stepKey) {
      case 'narrative':
        return (
          <>
            <Alert
              message="叙事配置"
              description="配置剧本的目标受众、难度和风格"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Form.Item
              label="目标受众"
              name={['narrative', 'target_audience']}
              initialValue="18-35岁"
            >
              <Input placeholder="例如：18-35岁女性玩家" />
            </Form.Item>
            <Form.Item
              label="难度等级"
              name={['narrative', 'difficulty_level']}
              initialValue="medium"
            >
              <Select>
                <Option value="easy">简单</Option>
                <Option value="medium">中等</Option>
                <Option value="hard">困难</Option>
                <Option value="expert">专家</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="写作风格"
              name={['narrative', 'writing_style']}
              initialValue="modern"
            >
              <Select>
                <Option value="modern">现代</Option>
                <Option value="classical">古典</Option>
                <Option value="suspense">悬疑</Option>
                <Option value="comedy">喜剧</Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="主题标签"
              name={['narrative', 'theme_tags']}
              initialValue={[]}
            >
              <Select mode="tags" placeholder="添加主题标签">
                <Option value="romance">浪漫</Option>
                <Option value="mystery">悬疑</Option>
                <Option value="scifi">科幻</Option>
                <Option value="fantasy">奇幻</Option>
              </Select>
            </Form.Item>
          </>
        )

      case 'space':
        return (
          <>
            <Alert
              message="空间配置"
              description="配置剧本中的空间数量和类型"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Form.Item
              label="公共空间数量范围"
              name={['space', 'public_space_range']}
              initialValue={[3, 5]}
            >
              <Slider range min={1} max={10} marks={{ 1: '1', 5: '5', 10: '10' }} />
            </Form.Item>
            <Form.Item
              label="私密空间数量范围"
              name={['space', 'private_space_range']}
              initialValue={[5, 10]}
            >
              <Slider range min={1} max={20} marks={{ 1: '1', 10: '10', 20: '20' }} />
            </Form.Item>
            <Form.Item
              label="阵营数量范围"
              name={['space', 'faction_count_range']}
              initialValue={[2, 4]}
            >
              <Slider range min={1} max={8} marks={{ 1: '1', 4: '4', 8: '8' }} />
            </Form.Item>
          </>
        )

      case 'npc':
        return (
          <>
            <Alert
              message="NPC 配置"
              description="配置非玩家角色的设置"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Form.Item
              label="NPC 模式"
              name={['npc', 'mode']}
              initialValue="optional"
            >
              <Radio.Group>
                <Radio value="disabled">禁用</Radio>
                <Radio value="optional">可选</Radio>
                <Radio value="required">必需</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label="NPC 数量范围"
              name={['npc', 'count_range']}
              initialValue={[2, 3]}
            >
              <Slider range min={0} max={10} marks={{ 0: '0', 5: '5', 10: '10' }} />
            </Form.Item>
          </>
        )

      case 'scale':
        return (
          <>
            <Alert
              message="规模配置"
              description="配置剧本的总人数和组数（支持灵活比例）"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  label="总玩家数"
                  name={['scale', 'total_player_count']}
                  initialValue={8}
                  rules={[{ required: true }]}
                >
                  <Input type="number" min={4} max={200} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="总演员数"
                  name={['scale', 'total_actor_count']}
                  initialValue={4}
                  rules={[{ required: true }]}
                >
                  <Input type="number" min={0} max={50} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="组数"
                  name={['scale', 'group_count']}
                  initialValue={2}
                  rules={[{ required: true }]}
                >
                  <Input type="number" min={1} max={10} />
                </Form.Item>
              </Col>
            </Row>
            <Alert
              message="灵活规模示例"
              description="10:10（恋陪本）、100:10（破冰本）、50:5（大型推理本）等任意比例"
              type="info"
              showIcon
            />
          </>
        )

      case 'persona':
        const scale = form.getFieldValue('scale') || { total_player_count: 8, total_actor_count: 4 }
        return (
          <>
            <Alert
              message="角色选择"
              description={`根据规模自动生成 ${scale.total_player_count} 个玩家角色和 ${scale.total_actor_count} 个演员角色`}
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Form.Item label="玩家默认模板">
              <Select
                placeholder="选择玩家模板"
                defaultValue={personaTemplates.playerTemplates[0]?.key}
              >
                {personaTemplates.playerTemplates.map((t) => (
                  <Option key={t.key} value={t.key}>
                    {t.name} - {t.description}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="演员默认模板">
              <Select
                placeholder="选择演员模板"
                defaultValue={personaTemplates.actorTemplates[0]?.key}
              >
                {personaTemplates.actorTemplates.map((t) => (
                  <Option key={t.key} value={t.key}>
                    {t.name} - {t.description}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Alert
              message="角色预览"
              description={`将生成：${scale.total_player_count} 个玩家角色，${scale.total_actor_count} 个演员角色`}
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className="script-generation-create-task-v5">
      {contextHolder}

      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
              返回
            </Button>
            <span>创建 AI 剧本生成任务（v5）</span>
          </Space>
        }
      >
        <Steps current={currentStep} style={{ marginBottom: 32 }}>
          {STEPS.map((step) => (
            <Step key={step.key} title={step.title} />
          ))}
        </Steps>

        <Form form={form} layout="vertical">
          {renderStepContent()}
        </Form>

        <div style={{ marginTop: 32, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={handlePrev}
            disabled={currentStep === 0}
            icon={<LeftOutlined />}
            size="large"
          >
            上一步
          </Button>

          <Button
            type="primary"
            onClick={handleNext}
            loading={loading}
            icon={currentStep === STEPS.length - 1 ? <CheckCircleOutlined /> : <RightOutlined />}
            size="large"
          >
            {currentStep === STEPS.length - 1 ? '开始生成' : '下一步'}
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default CreateTaskV5
